import { useCallback } from "react";
import { useApiUrl, useCustomMutation } from "@refinedev/core";
import { useSession } from "hooks/useSession";
import { useMediaAssetsStorage } from "hooks/useMediaAssetsStorage";
import type { ProjectInstanceSettingsRequest } from "pages/media/types";
import { DEFAULT_INSTANCE_ID } from "pages/experience-v2/constants";
import type { ExperienceLink } from "../components/config-cards/types";
import { mapLinksToCallToActions } from "../components/config-cards/general-settings/headerLinksMapping";

/**
 * Transmits the General card's "general" (non-add-on) settings to a
 * just-created project. Currently just header links: the `CallToActions`
 * asset doesn't exist until the links are saved, so this always creates a
 * fresh asset (never patches one) and points the instance at it via
 * `cta_group_id` — the same mechanism `LinksCtaSection` uses for a live
 * project.
 */
export const useApplyGeneralSettings = () => {
  const apiUrl = useApiUrl();
  const { mutateAsync } = useCustomMutation();
  const { currentOrganization } = useSession();
  const { saveCallToActionsAsset } = useMediaAssetsStorage({
    organizationId: currentOrganization?.id,
  });

  const applyHeaderLinks = useCallback(
    async (projectId: string, links: ExperienceLink[]) => {
      const actions = mapLinksToCallToActions(links);
      if (actions.length === 0) return;

      const assetId: string | undefined = await saveCallToActionsAsset(
        { actions },
        undefined,
        { category: "MediaMeta", description: "Experience CTAs" }
      );
      if (!assetId) return;

      await mutateAsync({
        url: `${apiUrl}/media/projects/${projectId}/instances/${DEFAULT_INSTANCE_ID}/settings`,
        method: "patch",
        values: {
          cta_group_id: assetId,
        } satisfies ProjectInstanceSettingsRequest,
        successNotification: false,
      });
    },
    [apiUrl, mutateAsync, saveCallToActionsAsset]
  );

  return { applyHeaderLinks };
};
